import { useCallback, useEffect, useRef } from 'react';

/**
 * Synchronisation entre l'onglet du diaporama et l'onglet du prompteur.
 *
 * POURQUOI BroadcastChannel ET PAS localStorage. Les deux marchent entre onglets
 * de même origine, mais `localStorage` prévient les autres onglets par un
 * événement `storage` qui n'est PAS émis dans l'onglet qui écrit, impose de
 * sérialiser en JSON dans une clé partagée, et laisse une trace persistante
 * qu'il faut nettoyer. BroadcastChannel est un canal éphémère, typé, et sans
 * effet de bord sur le stockage. Le repli `storage` reste écrit plus bas pour
 * les navigateurs qui ne l'ont pas — sur scène, on ne veut aucune surprise.
 *
 * ⚠️ LES DEUX ONGLETS DOIVENT ÊTRE DE MÊME ORIGINE. En développement, ouvrir le
 * prompteur sur `127.0.0.1` alors que le diaporama est sur `localhost` casse la
 * synchro sans aucun message d'erreur : ce sont deux origines différentes pour
 * le navigateur. Le prompteur affiche donc son état de connexion en clair.
 *
 * PROTOCOLE — volontairement minuscule, un seul message par changement :
 *   { t: 'etat',  index, num, total, titre, action, qr }  diaporama → prompteur
 *                                             (`action` : ce que jouera le prochain « suivant », ou null ;
 *                                              `qr` : le QR des questions est-il en grand à l'écran)
 *   { t: 'bonjour' }                          prompteur → diaporama (à l'ouverture)
 *   { t: 'nav',   delta }                     prompteur → diaporama (piloter à distance)
 *   { t: 'f1' }                               prompteur → diaporama (action F1)
 *   { t: 'qr',    actif }                     prompteur → diaporama (le QR des questions, en grand)
 *   { t: 'synthese' }                         diaporama → prompteur (la flèche de la slide 31 lance l'analyse)
 *   { t: 'adieu' }                            diaporama → prompteur (démontage)
 *
 * `bonjour` existe pour un cas très concret : le prompteur ouvert EN COURS de
 * conférence, ou rechargé. Sans lui, il resterait vide jusqu'au changement de
 * slide suivant — c'est-à-dire au pire moment.
 */

const prefixe = 'geopolitikia-conference';

export default function useConferenceSync(canal, onMessage) {
    const chanRef = useRef(null);
    const onMessageRef = useRef(onMessage);

    // La référence évite de refermer/rouvrir le canal à chaque rendu du parent :
    // un canal recréé perd les messages émis pendant la reconstruction.
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        const nom = `${prefixe}:${canal || 'defaut'}`;

        if (typeof BroadcastChannel !== 'undefined') {
            const bc = new BroadcastChannel(nom);
            bc.onmessage = (e) => onMessageRef.current?.(e.data);
            chanRef.current = {
                post: (msg) => bc.postMessage(msg),
                close: () => bc.close()
            };
            return () => {
                // ⚠️ ON COUPE LA RÉFÉRENCE AVANT DE FERMER. Sans ça, un effet de
                // nettoyage déclaré APRÈS celui-ci (React les exécute dans
                // l'ordre de déclaration) postait encore dans un canal fermé, et
                // `postMessage` lève `InvalidStateError`. En développement,
                // StrictMode monte puis démonte chaque composant : l'erreur
                // s'affichait donc en plein écran à l'ouverture de la conférence.
                chanRef.current = null;
                bc.close();
            };
        }

        // Repli : une clé jetable, réécrite à chaque message. L'onglet émetteur
        // ne reçoit pas son propre événement `storage`, ce qui est exactement le
        // comportement voulu.
        const cle = `${nom}:msg`;
        const surStorage = (e) => {
            if (e.key !== cle || !e.newValue) return;
            try {
                onMessageRef.current?.(JSON.parse(e.newValue).msg);
            } catch {
                /* message illisible : on l'ignore plutôt que de casser la scène */
            }
        };
        window.addEventListener('storage', surStorage);
        chanRef.current = {
            post: (msg) => {
                try {
                    // `n` force une valeur différente à chaque envoi : sans lui,
                    // deux messages identiques d'affilée (deux « nav +1 ») ne
                    // déclencheraient qu'un seul événement `storage`.
                    localStorage.setItem(cle, JSON.stringify({ n: Date.now() + Math.random(), msg }));
                } catch {
                    /* stockage indisponible (navigation privée) : on n'émet pas */
                }
            },
            close: () => window.removeEventListener('storage', surStorage)
        };
        return () => window.removeEventListener('storage', surStorage);
    }, [canal]);

    /* Un envoi ne doit JAMAIS interrompre la scène : si le canal vient d'être
       fermé, ou si l'onglet est en train de disparaître, on avale l'erreur. Le
       pire cas acceptable est un prompteur qui ne se met pas à jour ; un écran
       rouge devant quarante personnes ne l'est pas. */
    return useCallback((msg) => {
        try {
            chanRef.current?.post(msg);
        } catch {
            /* canal fermé : rien à faire, et surtout rien à propager */
        }
    }, []);
}
